"use client";

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { FileSignature, Lightbulb, Loader2, Scale, ShieldAlert } from "lucide-react";
import { getMyModuleTimeline } from "@/lib/modules.functions";
import { isTtpaPageVisible } from "@/lib/module-ttpa.functions";
import { HundertTageTimeline } from "@/components/cockpit/HundertTageTimeline";
import { HundertTagePhasePanel } from "@/components/cockpit/HundertTagePhasePanel";
import { isPhaseKey, type PhaseKey } from "@/lib/hundert-tage";
import { ChapterOverview } from "@/components/cockpit/ChapterOverview";
import { CockpitPageHeader } from "@/components/cockpit/CockpitPageHeader";
import { ModuleGuideDownload } from "@/components/cockpit/ModuleGuideDownload";
import { CockpitSection } from "@/components/cockpit/CockpitSection";
import { ModuleToolCard } from "@/components/cockpit/ModuleToolCard";

type ModuleSearch = { phase?: PhaseKey };

export const Route = createFileRoute("/_cockpit/cockpit/hundert-tage/")({
  validateSearch: (search: Record<string, unknown>): ModuleSearch => {
    const raw = typeof search.phase === "string" ? search.phase : null;
    return raw && isPhaseKey(raw) ? { phase: raw } : {};
  },
  component: HundertTageOverview,
});

function HundertTageOverview() {
  const { phase: phaseFromUrl } = Route.useSearch();
  const fetchTimeline = useServerFn(getMyModuleTimeline);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cockpit", "hundert-tage", "timeline"],
    queryFn: () => fetchTimeline(),
  });

  // Ticket Z13: Der Verweis erscheint nur, wenn der Server die Seite freigibt.
  const fetchTtpaVisible = useServerFn(isTtpaPageVisible);
  const { data: ttpaVisible } = useQuery({
    queryKey: ["cockpit", "hundert-tage", "ttpa-visible"],
    queryFn: () => fetchTtpaVisible(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Zeitachse wird geladen …
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground">
        Die Zeitachse konnte nicht geladen werden. Bitte versuchen Sie es später erneut.
      </p>
    );
  }

  // Ohne Auswahl in der Adresse steht die aktuelle Phase vorn.
  const selectedPhase: PhaseKey = phaseFromUrl ?? data.markerPhaseKey ?? "zwischenphase";

  return (
    <div className="space-y-8">
      {/* 1 Einstieg: Seitentitel (h1) und kurze Einordnung des Moduls. */}
      <CockpitPageHeader
        title="Ihr Fahrplan durch die 100 Tage"
        description="Der Fahrplan begleitet Sie von der Wahl bis zum hundertsten Tag im Amt: Die Zeitachse zeigt, wo Sie stehen – klicken Sie eine Phase an, und die zugehörigen Aufgaben erscheinen direkt darunter. Es gibt keine feste Reihenfolge."
      />

      {/* 2 Zeitachse: Ankerpunkt und Umschalter für die Aufgaben. */}
      <HundertTageTimeline timeline={data} selectedPhase={selectedPhase} />

      {/* 3 Aufgaben der gewählten Phase – aufgeklappt, ohne Unterseite. */}
      <HundertTagePhasePanel phase={selectedPhase} timeline={data} />

      {/* Kapitelübersicht: Phasenkapitel und Nachschlage-Beiträge zusammen. */}
      <CockpitSection
        headingId="hundert-tage-kapitel"
        label="Zum Lesen"
        title="Kapitel zum Lesen"
        intro="Alle Kapitel des Moduls auf einen Blick – von der Vorbereitung bis zum hundertsten Tag und Beiträge zu wiederkehrenden Fragen."
      >
        <ChapterOverview />
      </CockpitSection>

      {/* 5 Werkzeuge: Ideenpool (Z8), Vorlagen (Z9/Z11), TTPA (Z13). */}
      <CockpitSection
        headingId="hundert-tage-werkzeuge"
        label="Zum Arbeiten"
        title="Werkzeuge und Vorlagen"
        intro="Praktische Hilfen, die Sie unabhängig von der Phase nutzen können – jederzeit abrufbar."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ModuleToolCard
            to="/cockpit/hundert-tage/ideen"
            icon={Lightbulb}
            title="Ideenpool"
            description="Zwanzig Kommunikationsideen für Ihre 100 Tage – mit Fallstrick-Hinweis, Status und eigenem Notizfeld."
          />
          <ModuleToolCard
            to="/cockpit/hundert-tage/vorlagen/kaemmerei"
            icon={FileSignature}
            title="Vorlage Kämmerei"
            description="Ihre gewünschten Leistungen mit Vergütung als vorbereitetes Dokument – zum Einreichen in der Kämmerei, als PDF."
          />
          <ModuleToolCard
            to="/cockpit/hundert-tage/vorlagen/krisenplan"
            icon={ShieldAlert}
            title="Krisenplan"
            description="Erreichbarkeiten, Vertretungskette und Muster-Erststatement in einem Dokument – zum Aushängen, als PDF."
          />
          {ttpaVisible && (
            <ModuleToolCard
              to="/cockpit/hundert-tage/ttpa"
              icon={Scale}
              title="TTPA-Verordnung"
              description="Was die EU-Verordnung über Transparenz politischer Werbung für Ihr Amt bedeutet – und wie easyTTPA dabei unterstützt."
            />
          )}
        </div>
      </CockpitSection>

      {/* 6 Leitfaden zum Mitnehmen. */}
      <CockpitSection
        headingId="hundert-tage-leitfaden"
        label="Zum Mitnehmen"
        title="Leitfaden als Druckfassung"
        intro="Der gesamte Fahrplan als PDF – zum Ausdrucken oder Weitergeben im Team."
      >
        <ModuleGuideDownload />
      </CockpitSection>
    </div>
  );
}
